// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';

import { mapCourseHole, type CourseHole } from './useCourseHoles';

/**
 * One tee of a course, with the ratings a handicap is computed from.
 *
 * Rating and slope are per (tee, sex): the same box played from by a man and a woman carries
 * two rows, differing only in {@link sex}. Null where the club has published none — an
 * OSM-imported course has a tee position and no card at all.
 */
export type CourseTee = {
  id: string;
  name: string;
  /** "male" | "female" | "unisex" — the only thing telling two rows of one tee apart. */
  sex: string;
  teeColor: string;
  courseRating: number | null;
  slopeRating: number | null;
  par: number | null;
};

/** A course as its detail endpoint reports it: the catalogue entry, its tees and its holes. */
export type CourseCard = {
  id: string;
  name: string;
  clubName: string;
  city: string;
  visibility: string;
  lat: number | null;
  lon: number | null;
  tees: CourseTee[];
  holes: CourseHole[];
};

export type CourseCardState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; card: CourseCard }
  /** The endpoint answered 403, or the caller has no token: nothing to show, and not a fault. */
  | { status: 'forbidden' }
  | { status: 'failed' };

type Row = Record<string, unknown>;

const num = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const str = (row: Row, ...keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

const mapTee = (row: Row): CourseTee | null => {
  const id = str(row, 'id', 'Id');
  if (!id) return null;
  return {
    id,
    name: str(row, 'name', 'Name'),
    sex: str(row, 'sex', 'Sex'),
    teeColor: str(row, 'teeColor', 'TeeColor'),
    courseRating: num(row, 'courseRating', 'CourseRating'),
    slopeRating: num(row, 'slopeRating', 'SlopeRating'),
    par: num(row, 'par', 'Par'),
  };
};

/**
 * One course's full detail — the same `GET /api/geodata/courses/{id}` {@link useCourseHoles}
 * reads, kept whole rather than reduced to holes.
 *
 * Separate from that hook rather than replacing it: the map modal wants the holes and re-reads
 * them as the marker moves, and handing it a course object it does not use would make every
 * consumer of the holes depend on the catalogue fields too.
 */
export function useCourseCard(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null
): { state: CourseCardState; refresh: () => void } {
  const [state, setState] = useState<CourseCardState>({ status: 'idle' });
  const [reloads, setReloads] = useState(0);
  const refresh = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    if (!courseId) {
      setState({ status: 'idle' });
      return undefined;
    }
    if (!token) {
      setState({ status: 'forbidden' });
      return undefined;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed' });
          return;
        }
        const payload = (await response.json()) as Row;
        if (cancelled) return;
        const holeRows = payload.holes ?? payload.Holes;
        const teeRows = payload.tees ?? payload.Tees;
        setState({
          status: 'ready',
          card: {
            id: str(payload, 'id', 'Id') || courseId,
            name: str(payload, 'name', 'Name'),
            clubName: str(payload, 'clubName', 'ClubName'),
            city: str(payload, 'city', 'City'),
            visibility: str(payload, 'visibility', 'Visibility'),
            lat: num(payload, 'lat', 'Lat'),
            lon: num(payload, 'lon', 'Lon'),
            tees: Array.isArray(teeRows)
              ? (teeRows as Row[]).map(mapTee).filter((tee): tee is CourseTee => tee !== null)
              : [],
            holes: Array.isArray(holeRows)
              ? (holeRows as Row[])
                  .map(mapCourseHole)
                  .filter((hole): hole is CourseHole => hole !== null)
                  .sort((a, b) => a.number - b.number)
              : [],
          },
        });
      } catch {
        if (!cancelled) setState({ status: 'failed' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, token, reloads]);

  return { state, refresh };
}
